/**
 * Recuperação de senha.
 *
 * Mesma casca da tela de entrada (`TelaDeAcesso`), com o texto da esquerda virado para o
 * caminho: três passos numerados em vez dos diferenciais do produto.
 *
 * **A tela nunca diz se o e-mail existe.** Sucesso e falha levam ao mesmo texto ("se estiver
 * cadastrado"), e é de propósito: responder "esse e-mail não existe" transforma a recuperação de
 * senha num verificador de contas para quem está do lado de fora.
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  MailOutline,
  LockOutlined,
  MarkEmailReadOutlined,
  NorthEast,
} from '@mui/icons-material';

import branding from '../../config/branding';
import { authService } from '../../services/authService';
import { TelaDeAcesso, Numero, type ApoioDaHistoria } from '../../components/auth/TelaDeAcesso';
import {
  comAlfa,
  estiloDoBotao,
  estiloDoCampo,
  loginAccent,
  SERIFA,
  TEAL_LINK,
  TINTA,
  TINTA_SUAVE,
} from '../../components/auth/estilos';

const PASSOS: readonly ApoioDaHistoria[] = [
  {
    chave: '01',
    marca: <Numero valor="01" />,
    titulo: 'Informe seu e-mail',
    texto: 'Use o endereço cadastrado na plataforma.',
  },
  {
    chave: '02',
    marca: <Numero valor="02" />,
    titulo: 'Verifique sua caixa de entrada',
    texto: 'Enviaremos um link individual de recuperação.',
  },
  {
    chave: '03',
    marca: <Numero valor="03" />,
    titulo: 'Crie uma nova senha',
    texto: 'O link é temporário para proteger sua conta.',
  },
];

/** O quadrado arredondado que abre o cartão, com o ícone do momento. */
const Emblema: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'grid',
      placeItems: 'center',
      width: 54,
      height: 54,
      mx: 'auto',
      mb: 2,
      borderRadius: '20px',
      bgcolor: comAlfa(loginAccent, 0.12),
      color: loginAccent,
      '& svg': { fontSize: 26 },
    }}
  >
    {children}
  </Box>
);

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  const validateEmail = (): boolean => {
    if (!email) {
      setEmailError('E-mail é obrigatório');
      return false;
    }
    if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('E-mail inválido');
      return false;
    }
    setEmailError(null);
    return true;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!validateEmail()) {
      return;
    }

    setLoading(true);

    try {
      await authService.forgotPassword({ email });
    } catch {
      // Falha e sucesso levam ao mesmo lugar: ver o comentário do topo do arquivo.
    } finally {
      setLoading(false);
      setSuccess(true);
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    if (emailError) {
      setEmailError(null);
    }
  };

  return (
    <TelaDeAcesso
      titulo="Recupere seu acesso."
      destaque="Continue no controle."
      subtitulo="Um processo rápido e protegido para você voltar à gestão da sua operação."
      apoios={PASSOS}
      selo={['Segurança em cada etapa.', 'Autonomia para continuar.']}
    >
      {success ? (
        <Box sx={{ textAlign: 'center' }}>
          <Emblema>
            <MarkEmailReadOutlined />
          </Emblema>
          <Typography
            component="h2"
            sx={{
              fontFamily: SERIFA,
              fontWeight: 700,
              fontSize: { xs: '1.7rem', md: '2rem' },
              letterSpacing: '-0.02em',
              color: TINTA,
            }}
          >
            Confira seu e-mail
          </Typography>
          <Typography sx={{ mt: 1, color: TINTA_SUAVE, fontSize: '1.02rem' }}>
            Se estiver cadastrado, enviaremos um link para redefinir sua senha.
          </Typography>
          <Typography
            sx={{ mt: 1.5, mb: 'clamp(16px, 2.8vh, 28px)', color: TINTA_SUAVE, fontSize: '0.88rem' }}
          >
            Não encontrou? Verifique também a caixa de spam.
          </Typography>

          <Button
            component={Link}
            to="/login"
            fullWidth
            endIcon={<NorthEast sx={{ fontSize: 18 }} />}
            sx={estiloDoBotao}
          >
            Voltar para o login
          </Button>

          {/* Errar o e-mail é o motivo mais comum de a mensagem não chegar. Sem esta saída, a
              única forma de tentar outro endereço é recarregar a página na mão. */}
          <Button
            onClick={() => {
              setSuccess(false);
              setEmail('');
            }}
            fullWidth
            sx={{
              mt: 1.5,
              textTransform: 'none',
              fontWeight: 700,
              color: TEAL_LINK,
              '&:hover': { bgcolor: comAlfa(loginAccent, 0.08) },
            }}
          >
            Enviar para outro e-mail
          </Button>
        </Box>
      ) : (
        <>
          <Box
            component={Link}
            to="/login"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.75,
              mb: 'clamp(12px, 2.4vh, 24px)',
              color: TINTA_SUAVE,
              fontSize: '0.92rem',
              textDecoration: 'none',
              '&:hover': { color: TEAL_LINK },
            }}
          >
            <ArrowBack sx={{ fontSize: 18 }} />
            Voltar para o login
          </Box>

          <Box sx={{ textAlign: 'center' }}>
            <Emblema>
              <LockOutlined />
            </Emblema>
            <Typography
              component="h1"
              sx={{
                fontFamily: SERIFA,
                fontWeight: 700,
                fontSize: { xs: '1.7rem', md: '2rem' },
                letterSpacing: '-0.02em',
                color: TINTA,
              }}
            >
              Esqueceu sua senha?
            </Typography>
            <Typography
              sx={{
                mt: 0.75,
                mb: 'clamp(16px, 2.8vh, 30px)',
                color: TINTA_SUAVE,
                fontSize: '1.02rem',
              }}
            >
              Digite o e-mail cadastrado. Você receberá um link para criar uma nova senha.
            </Typography>
          </Box>

          <Box component="form" onSubmit={handleSubmit} noValidate>
            <Typography
              component="label"
              htmlFor="email"
              sx={{ display: 'block', mb: 0.875, fontWeight: 700, color: TINTA }}
            >
              E-mail
            </Typography>
            <TextField
              fullWidth
              id="email"
              name="email"
              type="email"
              placeholder="seu@email.com"
              value={email}
              onChange={handleEmailChange}
              error={!!emailError}
              helperText={emailError}
              disabled={loading}
              autoComplete="email"
              autoFocus
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <MailOutline sx={{ color: '#71809a' }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={estiloDoCampo}
            />

            <Typography
              sx={{
                mt: 1.25,
                mb: 'clamp(14px, 2.6vh, 26px)',
                color: '#728099',
                fontSize: '0.84rem',
                lineHeight: 1.45,
              }}
            >
              Por segurança, enviaremos as instruções somente para o e-mail vinculado à conta.
            </Typography>

            <Button
              type="submit"
              fullWidth
              disabled={loading}
              endIcon={loading ? undefined : <NorthEast sx={{ fontSize: 18 }} />}
              sx={estiloDoBotao}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Enviar link de recuperação'
              )}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 'clamp(16px, 2.8vh, 28px)' }}>
            <Typography sx={{ mb: 0.75, fontWeight: 700, color: TINTA }}>
              Não lembra qual e-mail utilizou?
            </Typography>
            <Typography
              component="a"
              href={`mailto:${branding.supportEmail}`}
              sx={{
                fontWeight: 700,
                color: TEAL_LINK,
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline', textUnderlineOffset: 3 },
              }}
            >
              Fale com o suporte →
            </Typography>
          </Box>
        </>
      )}
    </TelaDeAcesso>
  );
};

export default ForgotPasswordPage;
